import * as React from 'react';
import { Link, useNavigate } from 'react-router';
import { AccountsError, lookupInvitation, signupByInvitation, type InvitationLookup } from '../accounts.ts';
import { api } from '../../platform/index.ts';
import { ensureRuntimeSession, runtimeBase } from '../runtime-session.ts';
import { roleLabel } from '../roles.ts';
import { useAuth } from '../auth.tsx';
import { errorMessage, unwrap } from '../admin/common.tsx';
import { AUTH_LINK, AUTH_SUBMIT, AuthField, AuthNotice, AuthShell, useFragmentToken } from './AuthShell.tsx';
import { MIN_PASSWORD, passwordProblem } from './ResetPassword.tsx';

/** Where the token waits while an invitee with an account signs in (this tab only). */
export const INVITE_STASH = 'cortex_invite_token';
const GONE = 'This invitation is not valid any more. Ask your administrator for a new one.';

/**
 * Where an accepted app invitation may send the browser (ADR 0044 §4): its `redirect`, only when it is on the app
 * runtime's own origin (`runtime`, resolved against this page's `origin`); anything else is null (no open
 * redirect), and so is a deployment without a runtime.
 */
export function appLanding(
  redirect: string | null | undefined,
  runtime: string,
  origin: string,
): string | null {
  if (!redirect || !runtime) return null;
  try {
    const base = new URL(runtime, origin);
    const to = new URL(redirect, base);
    if (to.origin !== base.origin || (to.protocol !== 'https:' && to.protocol !== 'http:')) return null;
    return to.toString();
  } catch {
    return null;
  }
}

/** An app's share link on this web (`/p/<project>/b/<branch>/share/apps/<app>`). */
const SHARE_LINK = /^\/p\/[^/?#]+\/b\/[^/?#]+\/share\/apps\/[^/?#]+\/?$/;

/**
 * The landing on this web an accepted invitation may send the browser to: an app user's share link (the app full
 * screen), as a path of this origin; anything else (another origin, `//host`, any other page) is null.
 */
export function webLanding(redirect: string | null | undefined, origin: string): string | null {
  if (!redirect || !redirect.startsWith('/') || redirect.startsWith('//')) return null;
  try {
    const to = new URL(redirect, origin);
    if (to.origin !== new URL(origin).origin || !SHARE_LINK.test(to.pathname)) return null;
    return `${to.pathname}${to.search}${to.hash}`;
  } catch {
    return null;
  }
}

/**
 * The page an invitation link opens (`/invite#token=…`, ADR 0014 §10). Someone with no account creates it
 * here with a password (sign-up exists only this way); someone with an account signs in and accepts it;
 * an organization that signs in with single sign-on is joined through its sign-in button. An app's invitation
 * (ADR 0044 §4) works the same way and ends in the app: the runtime gets the new session first, then the browser
 * goes to the app's address.
 */
export function InviteAcceptPage() {
  const token = useFragmentToken(INVITE_STASH);
  const { state, signedIn } = useAuth();
  const navigate = useNavigate();
  const [inv, setInv] = React.useState<InvitationLookup | null>(null);
  const [error, setError] = React.useState(token ? '' : GONE);
  const [dead, setDead] = React.useState(!token);
  const [first, setFirst] = React.useState('');
  const [last, setLast] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [confirm, setConfirm] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (!token) return;
    let cancelled = false;
    lookupInvitation(token)
      .then((r) => !cancelled && setInv(r))
      .catch((e) => {
        if (cancelled) return;
        setDead(true);
        setError(
          e instanceof AccountsError && e.status === 404
            ? GONE
            : errorMessage(e, 'The invitation could not be read'),
        );
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const finish = () => {
    try {
      sessionStorage.removeItem(INVITE_STASH);
    } catch {
      /* nothing kept */
    }
    navigate('/', { replace: true });
  };
  /**
   * Where an accepted invitation ends: an app user's share link on this web (the app full screen: an app user never
   * sees cortex itself); an app invitation's app on the runtime (the runtime signs the new session in first); else
   * cortex itself.
   */
  const land = async (redirect: string | null) => {
    const web = webLanding(redirect, window.location.origin);
    if (web) {
      try {
        sessionStorage.removeItem(INVITE_STASH);
      } catch {
        /* nothing kept */
      }
      return navigate(web, { replace: true });
    }
    const to = appLanding(redirect, runtimeBase(), window.location.origin);
    if (!to) return finish();
    try {
      sessionStorage.removeItem(INVITE_STASH);
    } catch {
      /* nothing kept */
    }
    // A runtime that refuses or is down shows its own sign-in page at the address; nothing better to do here.
    await ensureRuntimeSession({ fresh: true }).catch(() => undefined);
    window.location.assign(to);
  };
  // An app invitation, or an app user's organization invitation riding on app shares: both are to apps, named as such.
  const isApp = inv?.kind === 'app' || Boolean(inv?.appTitle);

  const signup = async (e: React.FormEvent) => {
    e.preventDefault();
    const p = passwordProblem(password, confirm);
    if (p) return setError(p);
    setBusy(true);
    setError('');
    try {
      const s = await signupByInvitation({
        token,
        password,
        ...(first.trim() ? { first_name: first.trim() } : {}),
        ...(last.trim() ? { last_name: last.trim() } : {}),
      });
      await signedIn(s.session);
      if (s.redirect) await land(s.redirect);
      else finish();
    } catch (err) {
      if (err instanceof AccountsError && err.status === 404) {
        setDead(true);
        setError(GONE);
      } else setError(err instanceof AccountsError ? err.message : 'The sign-up failed.');
    } finally {
      setBusy(false);
    }
  };

  const signedInAs = state.status === 'signed-in' ? state.user.email.toLowerCase() : null;
  const accept = async () => {
    if (!inv) return;
    if (!signedInAs) return navigate('/login', { state: { from: { pathname: '/invite' } } });
    setBusy(true);
    setError('');
    try {
      if (inv.kind === 'app') {
        const r = await unwrap(
          api.POST('/api/app-invitations/{id}/accept', { params: { path: { id: inv.id } } }),
        );
        await land(r.redirect);
        return;
      }
      const r = await unwrap(api.POST('/api/invitations/{id}/accept', { params: { path: { id: inv.id } } }));
      await land(r.redirect ?? null);
    } catch (err) {
      setError(errorMessage(err, 'The invitation could not be accepted'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      title="You are invited"
      subtitle={
        inv ? (isApp ? `Open ${inv.appTitle} on Cortex` : `Join ${inv.orgName} on Cortex`) : undefined
      }
      testId="invite-page"
    >
      {dead ? (
        <div className="flex flex-col gap-5">
          <AuthNotice kind="error" testId="invite-error">
            {error}
          </AuthNotice>
          <Link to="/login" className={AUTH_LINK}>
            Go to sign in
          </Link>
        </div>
      ) : !inv ? (
        <div className="text-sm text-page-muted">Reading the invitation…</div>
      ) : (
        <div className="flex flex-col gap-5">
          <div className="text-sm text-page-text flex flex-col gap-1">
            {isApp ? (
              <span>
                App: <strong data-testid="invite-app">{inv.appTitle}</strong>
              </span>
            ) : (
              <>
                <span>
                  Organization: <strong data-testid="invite-org">{inv.orgName}</strong>
                </span>
                <span>Role: {roleLabel(inv.role)}</span>
              </>
            )}
            <span>
              Email:{' '}
              <span className="font-mono text-xs" data-testid="invite-email">
                {inv.email}
              </span>
            </span>
          </div>
          {error && (
            <AuthNotice kind="error" testId="invite-error">
              {error}
            </AuthNotice>
          )}
          {inv.ssoRequired ? (
            <AuthNotice kind="ok">
              {isApp
                ? 'Your address signs in with single sign-on. Use its button on the sign-in page, then open this link again.'
                : `${inv.orgName} signs in with single sign-on. Use its button on the sign-in page; the invitation is waiting in your profile afterwards.`}
            </AuthNotice>
          ) : inv.signup ? (
            <form onSubmit={signup} className="flex flex-col gap-5">
              <AuthField
                id="invite-first"
                label="First name"
                autoComplete="given-name"
                value={first}
                onChange={(e) => setFirst(e.target.value)}
                testId="invite-first"
              />
              <AuthField
                id="invite-last"
                label="Last name"
                autoComplete="family-name"
                value={last}
                onChange={(e) => setLast(e.target.value)}
                testId="invite-last"
              />
              <AuthField
                id="invite-password"
                label="Password"
                type="password"
                autoComplete="new-password"
                required
                minLength={MIN_PASSWORD}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                testId="invite-password"
              />
              <AuthField
                id="invite-confirm"
                label="Confirm the password"
                type="password"
                autoComplete="new-password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                testId="invite-confirm"
              />
              <button type="submit" disabled={busy} data-testid="invite-submit" className={AUTH_SUBMIT}>
                {busy
                  ? 'Creating your account…'
                  : isApp
                    ? 'Create account and open the app'
                    : 'Create account and join'}
              </button>
            </form>
          ) : inv.hasAccount ? (
            <>
              {signedInAs && signedInAs !== inv.email.toLowerCase() && (
                <AuthNotice kind="error">
                  You are signed in as {signedInAs}. Sign out and sign in as {inv.email} to accept.
                </AuthNotice>
              )}
              <button
                type="button"
                disabled={busy || Boolean(signedInAs && signedInAs !== inv.email.toLowerCase())}
                onClick={() => void accept()}
                data-testid="invite-signin"
                className={AUTH_SUBMIT}
              >
                {signedInAs
                  ? isApp
                    ? 'Open the app'
                    : 'Accept the invitation'
                  : isApp
                    ? 'Sign in to open the app'
                    : 'Sign in to accept'}
              </button>
            </>
          ) : (
            <AuthNotice kind="error" testId="invite-error">
              Sign-up is not available here. Ask your administrator.
            </AuthNotice>
          )}
        </div>
      )}
    </AuthShell>
  );
}
